import React from "react";
import { Icon, Select, SelectOption, Button, Menu, MenuItem } from "@showitinc/showit-wc";
import AppConfig from "../../config/app";
import UserSiteChooserManager from "./UserSiteChooserManager";
import styles from "./UserSiteChooserView.module.css";

const UserSiteChooserHeader = ({
  personalFolders,
  selectedFolder,
  onFolderSelect,
  siteQuery,
  onSiteQuery,
  siteSort,
  onSiteSort,
  isFilterDefault,
  onClearFilters,
  onCreateNewSite,
  closeEnabled,
  onClose,
  selectedSites,
  allFolders,
  onAddFolder,
  onMoveSites,
  onDeselectAll,
  onRenameFolder,
  onDeleteFolder,
}) => {
  /**
   * Transforms folders for navigation dropdown
   * @returns {Array} Array of folder options for navigation
   */
  const _transformFoldersForNavigation = () => {
    const personalFoldersOnly = allFolders.filter((folder) => folder.name !== "Uncategorized");
    return personalFoldersOnly.map((folder) => ({
      value: folder.folderId,
      label: folder.name,
      iconType: "folderClosed",
    }));
  };

  // prevents nav select from closing when clicking on the context menu button
  const handleMenuRef = (element) => {
    if (element) {
      const stopProp = (e) => {
        const isMenuItemClick = e.target.closest("si-menu-item");
        if (!isMenuItemClick) {
          e.stopPropagation();
        }
      };
      element.addEventListener("click", stopProp);
    }
  };

  /**
   * Transforms folders for the manager component
   * @returns {Array} Array of folder options for manager
   */
  const _transformFoldersForManager = () => {
    const foldersToShow =
      personalFolders.length > 0
        ? allFolders
        : allFolders.filter((folder) => folder.name !== "Uncategorized");

    return foldersToShow.map((folder) => {
      const hasAssigned = selectedSites.some((site) => {
        if (folder.name === "Uncategorized") {
          return !site.folder_id || !allFolders.find((f) => f.folderId === site.folder_id);
        }
        return site.folder_id === folder.folderId;
      });
      return {
        value: folder.folderId,
        label: folder.name,
        iconType: "folderClosed",
        hasAssigned,
      };
    });
  };

  const _handleFolderSelect = (e) => {
    const selectedValue = e.detail?.value;
    if (selectedValue === "add-new-folder") {
      onAddFolder();
    } else {
      onFolderSelect(e);
    }
  };

  const handleRenameClick = (e, option) => {
    e.stopPropagation();
    onRenameFolder(option);
  };

  const handleDeleteClick = (e, option) => {
    e.stopPropagation();
    onDeleteFolder(option);
  };

  return (
    <div className={styles["site-chooser-header"]}>
      {personalFolders.length > 0 && (
        <Select
          value={selectedFolder}
          onSISelect={_handleFolderSelect}
          className={styles["folder-select"]}
          variant="select-icon"
          showIcon
          hideLabel
        >
          <SelectOption value="all-site-designs" className={styles["all-site-designs-option"]}>
            All Site Designs
          </SelectOption>
          {_transformFoldersForNavigation().map((option) => (
            <SelectOption
              key={option.value}
              value={option.value}
              icon={option.iconType}
              iconSize="20"
            >
              {option.label}
              <Menu slot="suffix" variant="icon" py="lg" ref={handleMenuRef}>
                <Button
                  variant="icon"
                  icon-prefix="menuMoreVert"
                  iconSize="16"
                  slot="trigger-element"
                  className={styles["context-menu-button"]}
                />
                <MenuItem onClick={(e) => handleRenameClick(e, option)}>Rename Folder</MenuItem>
                <MenuItem onClick={(e) => handleDeleteClick(e, option)}>
                  <span className={styles["delete-text"]}>Delete Folder</span>
                </MenuItem>
              </Menu>
            </SelectOption>
          ))}
          <SelectOption
            value="uncategorized"
            icon="folderClosed"
            iconSize="20"
            className={styles["uncategorized-option"]}
          >
            Uncategorized
          </SelectOption>
          <SelectOption divider></SelectOption>
          <SelectOption
            value="add-new-folder"
            icon="add"
            iconSize="16"
            keepOpen
            className={styles["add-new-folder-option"]}
          >
            Add New Folder
          </SelectOption>
        </Select>
      )}
      <div className={styles["search-input-wrapper-inner"]}>
        <Icon className={styles["search-input-icon"]} type="search" size="20" />
        <input
          type="text"
          placeholder="Search designs"
          value={siteQuery}
          onChange={onSiteQuery}
          className={styles["search-input"]}
        />
      </div>
      <Select
        label="Sort By: "
        value={siteSort}
        onSISelect={onSiteSort}
        className={styles["sortby-field"]}
      >
        <SelectOption value="last_access">Recent</SelectOption>
        <SelectOption value="create_date">Date Created</SelectOption>
        <SelectOption value="name">Name ( A-Z )</SelectOption>
      </Select>
      {!isFilterDefault && (
        <button
          type="button"
          title="clear"
          className={styles["site-chooser-clear-button"]}
          onClick={onClearFilters}
        >
          <Icon type="close" />
        </button>
      )}
      <div className={styles["create-new-site-button-wrapper"]}>
        {!AppConfig.demoMode && (
          <Button
            variant="feature"
            size="lg"
            className={styles["create-new-site-button"]}
            onClick={onCreateNewSite}
          >
            Create A New Design
          </Button>
        )}
      </div>
      {closeEnabled ? (
        <button
          type="button"
          className={`glyphicon glyphicon-remove_2 ${styles["site-chooser-close-button"]}`}
          onClick={onClose}
        />
      ) : null}
      <div className={styles["site-folder-manager-container"]}>
        <UserSiteChooserManager
          selectedSites={selectedSites}
          folders={_transformFoldersForManager()}
          onAddFolder={onAddFolder}
          onMoveSites={onMoveSites}
          onDeselectAll={onDeselectAll}
        />
      </div>
    </div>
  );
};

export default UserSiteChooserHeader;
